import { useCallback, useEffect } from 'react';

import { useNavigate, useParams } from '@tanstack/react-router';
import orderBy from 'lodash.orderby';

import { fileFormToUpdate, useMedia } from '@/_containers';
import { useEdit, useInvalidate, usePostAuditLog, useProfileCrudActions } from '@/_shared';

import { useDeleteMutation, useGetRegistrar, useUpdateMutation } from './apis';
import type { RegistrarFormSchema, RegistrarQuerySchema, RegistrarUpdateSchema } from './schemas';
import { useFormDefinition } from './use-form';

export function usePage() {
  const edit = useEdit();
  const form = useFormDefinition();

  const { id } = useParams({ strict: false });
  usePostAuditLog({ endpoint: 'registrars', id: `${id}` });
  const nav = useNavigate();

  const registrar = useGetRegistrar(id ?? '');
  const { setHasToInvalidate, removeQuery } = useInvalidate(['REGISTRAR', id ?? '']);
  const updateMutation = useUpdateMutation(id ?? '');
  const deleteMutation = useDeleteMutation();
  const formToMutation = useCallback(
    ({ phone_numbers, domains, logo, ...props }: RegistrarFormSchema): RegistrarUpdateSchema => ({
      ...props,
      phone_numbers: phone_numbers?.map((item) => item.id),
      logo: fileFormToUpdate(logo),
      domains: domains?.map((item) => item.id) ?? [],
    }),
    []
  );

  const { onUpdate, onDelete, isDeleting, isPending, isSuccess } = useProfileCrudActions<
    RegistrarFormSchema,
    RegistrarUpdateSchema,
    { data: RegistrarQuerySchema }
  >({
    form,
    edit,
    formToMutation,
    updateMutation,
    deleteMutation,
    deleteVariables: { id: `${id}` },
    updateSuccessMessage: 'Registrar has been updated.',
    deleteSuccessMessage: 'Registrar has been deleted.',
    successToastId: '',
    onSuccessSideEffects: () => {
      removeQuery(['REGISTRARS']);
      setHasToInvalidate.setTrue();
    },
    onDeleteSuccess: () => {
      nav({ to: '/settings/registrars' });
    },
  });

  const onSubmit = useCallback(
    (values: RegistrarFormSchema) => {
      onUpdate(formToMutation(values));
    },
    [formToMutation, onUpdate]
  );

  const { onImageUpdate } = useMedia(onUpdate);

  useEffect(() => {
    const { data: response } = registrar;
    if (response) {
      const { data } = response;
      form.reset({
        ...data,
        name: data.name ?? '',
        url: data.url ?? '',
        notes: data.notes ?? '',
        id: data.id,
        phone_numbers: data?.phone_numbers ?? [],
        domains: orderBy(data?.domains ?? [], ['name']),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrar.data]);

  console.log(form.formState.errors);

  return {
    id,
    form,
    isPending: isPending || deleteMutation.isPending,
    isSuccess,
    onSubmit,
    edit,
    onDelete,
    isDeleting,
    onImageUpdate,
    onUpdate,
  };
}
